import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { BillingDataRoomInfo, EditUserTypeRel, InviteUsers, BillingRequestRestore, BillingAddComment } from 'modules/Rest'
import Layout from './Layout'
import GridContainer from './GridContainer'
import GridCard from './GridCard'
import CardHeader from './CardHeader'
import { DataRoomsContext, MessageContext } from './Contexts'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { LoadingDotsIcon } from './Icons'
import AddIcon from '@mui/icons-material/AddCircleOutline'
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft'
import { format, isValid } from 'date-fns'
import AButton from './AButton'
import { AddUsersDialog } from './AddUsersDialog'
import { AddCommentDialog } from './AddCommentDialog'
import ActionButtons, { DirectActionButton } from './ActionButtons'
import { setModes } from 'modules/Utils'

const formatDate = (time) => {
  if (time == null) {
    return '—'
  }

  const date = new Date(time)
  return isValid(date) ? format(date, 'yyyy-MM-dd') : '—'
}

const useStyles = makeStyles(() => ({
  loadingDots: {
    paddingBottom: 10,
    paddingLeft: 10,
    display: 'inline-block',
  },
  headingCell: {
    fontWeight: 500,
  },
  wMargin: {
    marginTop: 20,
  },
  btnCell: {
    textAlign: 'right',
    paddingRight: 0,
    '&:last-child': {
      paddingRight: 0,
    },
    paddingTop: 1,
    paddingBottom: 1,
  },
  noUsers: {
    fontStyle: 'italic',
  }
}))

export default function BillingDataRoom() {
  const { setRefreshNext } = useContext(DataRoomsContext)
  const classes = useStyles()
  const navigate = useNavigate()
  const { dataRoomKey } = useParams()
  const { messageDispatch } = useContext(MessageContext)
  const [loading, setLoading] = useState(false)
  const [dataRoomInfo, setDataRoomInfo] = useState(null)
  const [modeAddAdmins, setModeAddAdmins] = useState('')
  const [modeAddComment, setModeAddComment] = useState('')
  const [modeRemoveUser, setModeRemoveUser] = useState('')
  const [selectedUser, setSelectedUser] = useState({})

  const getDataRoomInfo = useCallback(async () => {
      try {
        setLoading(true)
        const { data: { success, info, ...data } } = await BillingDataRoomInfo({data_room: dataRoomKey})
        if (success) {
          setDataRoomInfo(data)
        } else {
          throw new Error(info ? info : 'An error occurred')
        }
      } catch (error) {
        messageDispatch({ type: 'warning', payload: error.message })
      }
      setLoading(false)
  }, [messageDispatch, dataRoomKey])

  const inviteCallback = useCallback(() => {
    getDataRoomInfo()
    setRefreshNext(true)
  }, [getDataRoomInfo, setRefreshNext])

  const commentCallback = useCallback(() => {
    getDataRoomInfo()
  }, [getDataRoomInfo])

  const removeUser = (user) => {
    if (user.is_current_user) {
      setSelectedUser(user)
      setModeRemoveUser('editing')
    } else {
      doRemoveUser(user, false)
    }
  }
  const doRemoveUser = useCallback(async (user, force) => {
    try {
      const { data: { success, info } } = await EditUserTypeRel({...user, user_type: dataRoomInfo.admin_user_type, remove: true, force: force})
      if (success) {
        getDataRoomInfo()
        if (user.is_current_user) {
          await setModes(setModeRemoveUser, ['completing', 1500, ''])
          setRefreshNext(true)
        }
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [dataRoomInfo, getDataRoomInfo, messageDispatch, setRefreshNext])

  const requestRestore = useCallback(async() => {
    try {
      const { data: { success, info } } = await BillingRequestRestore({data_room: dataRoomKey})
      if (success) {
        messageDispatch({type: 'info', payload: 'The restore request has been submitted'})
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [dataRoomKey, messageDispatch])

  useEffect(() => {
    getDataRoomInfo()
  }, [getDataRoomInfo])

  return <Layout showRoomSelect={false}>
    <GridContainer>
      <GridCard sm={12} uniformPadding={true}>
        <CardHeader
          title={<>
            <>
              {dataRoomInfo && dataRoomInfo.data_room_display_name}
              {dataRoomInfo && dataRoomInfo.light && <span style={{ fontStyle: 'italic' }}> Light</span>}
            </>
            {loading ? <div className={classes.loadingDots}><LoadingDotsIcon size={6} vPadding={6} hPadding={6}/></div> : null}
          </>}
          headingElement='h5'
          buttons={[<AButton
            startIcon={<SubdirectoryArrowLeftIcon/>}
            onClick={() => navigate(-1)}
            style={{ float: 'right' }}
          >
            Back
          </AButton>]}
        />
        {dataRoomInfo && <TableContainer>
          <Table size='small'>
            <TableBody>
              <TableRow>
                <TableCell className={classes.headingCell}>Billing ID</TableCell>
                <TableCell>{dataRoomInfo.billing_id}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Data Room ID</TableCell>
                <TableCell>{dataRoomInfo.data_room_id}</TableCell>
                <TableCell/>
              </TableRow>

              <TableRow>
                <TableCell className={classes.headingCell}>Created</TableCell>
                <TableCell>{formatDate(dataRoomInfo.creation_time)} by {dataRoomInfo.created_by}</TableCell>
                <TableCell/>
              </TableRow>
              {(dataRoomInfo.billing_details || dataRoomInfo.show_billing_details) && <TableRow>
                <TableCell className={classes.headingCell}>Billing details</TableCell>
                <TableCell style={{ whiteSpace: 'pre-line' }}>
                  <div>{dataRoomInfo.billing_details}</div>
                  {dataRoomInfo.billing_comments.map((comment_i, index) => <div key={index}>{comment_i}</div>)}
                </TableCell>
                {dataRoomInfo.show_billing_details ? <TableCell className={classes.btnCell}>
                  <AButton
                    startIcon={<AddIcon/>}
                    onClick={() => setModeAddComment('editing')}
                    disabled={false}
                  >
                    Add billing comment
                  </AButton>
                </TableCell> : <TableCell/>}
              </TableRow>}
              <TableRow>
                <TableCell className={classes.headingCell}>Previous renewals</TableCell>
                <TableCell>
                  {dataRoomInfo.renewals.map((renewal_i, index) => <div key={index}>
                    {formatDate(renewal_i.time)}&nbsp;&nbsp;{renewal_i.type}
                  </div>)}
                </TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Next renewal</TableCell>
                <TableCell>{formatDate(dataRoomInfo.next_renewal_time)}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Q&A</TableCell>
                <TableCell>{dataRoomInfo.qa ? 'Yes' : 'No'}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Enabled</TableCell>
                <TableCell>{!dataRoomInfo.disabled ? 'Yes' : <>No. Disabled by {dataRoomInfo.disabled_by || '—'} on {formatDate(dataRoomInfo.disabled_time)}</>}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Deleted</TableCell>
                <TableCell>{dataRoomInfo.deleted ? <>Yes, by {dataRoomInfo.deleted_by || '—'} on {formatDate(dataRoomInfo.deleted_time)}</> : 'No'}</TableCell>
                <TableCell/>
              </TableRow>
              {dataRoomInfo.deleted && <TableRow>
                <TableCell className={classes.headingCell}>Restorable</TableCell>
                <TableCell>{dataRoomInfo.restorable ? <>Yes, until {formatDate(dataRoomInfo.restorable)}</> : 'No'}</TableCell>
                <TableCell className={classes.btnCell}>
                  <DirectActionButton actionTitle='Request restore' action={requestRestore} buttonParams={{ disabled: !dataRoomInfo.restorable}} extraModes={['completing', 60000]}/>
                </TableCell>
              </TableRow>}
            </TableBody>
          </Table>
          <AddCommentDialog
            mode={modeAddComment}
            setMode={setModeAddComment}
            callback={commentCallback}
            commentFn={BillingAddComment}
            params={{ data_room: dataRoomKey }}
          />
        </TableContainer>}

        {dataRoomInfo && dataRoomInfo.admin_users && dataRoomInfo.admin_users.length ? <TableContainer>
          <Table size='small' className={classes.wMargin}>
            <TableHead>
              <TableRow>
                <TableCell>Administrator(s)</TableCell>
                <Tooltip title={dataRoomInfo.allow_add_edit_dr_admins ? '' : 'The action has not been enabled'} placement="top-end">
                  <TableCell className={classes.btnCell}>
                    <AButton
                      startIcon={<AddIcon/>}
                      onClick={() => setModeAddAdmins('editing')}
                      disabled={dataRoomInfo.deleted || !dataRoomInfo.allow_add_edit_dr_admins}
                    >
                      Add administrators
                    </AButton>
                  </TableCell>
                </Tooltip>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataRoomInfo.admin_users.length ? dataRoomInfo.admin_users.map((user_i, index) => (
                <TableRow key={user_i.user_name}>
                  <TableCell>{user_i.user_name}</TableCell>
                  <TableCell className={classes.btnCell}>
                    {user_i.more_info && <AButton onClick={() => navigate(`/billing-user/${user_i.user_name}`)} style={{marginRight: 10}}>More info</AButton>}
                    {dataRoomInfo.allow_add_edit_dr_admins && <DirectActionButton
                      actionTitle='Remove'
                      color='error'
                      action={() => removeUser(user_i)}
                      buttonParams={{ disabled: dataRoomInfo.deleted}}
                      extraModes={user_i.is_current_user ? [] : ['completing', 1500]}
                    />}
                  </TableCell>
                </TableRow>
              )) : <TableRow><TableCell className={classes.noUsers}>No administrators</TableCell><TableCell/></TableRow>}
            </TableBody>
          </Table>
          <AddUsersDialog
            mode={modeAddAdmins}
            setMode={setModeAddAdmins}
            callback={inviteCallback}
            inviteFn={InviteUsers}
            params={{ user_type: dataRoomInfo.admin_user_type }}
            heading='Add administrators'
          />

          <Dialog open={!!modeRemoveUser}>
            <DialogTitle>Confirmation request</DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <Typography variant="body2">
                Would you like to remove yourself as administrator of the data room?
              </Typography>
              <Typography variant="body2">
                You will lose all access to the data room.
              </Typography>
            </DialogContent>
            <DialogActions>
              <ActionButtons
                mode={modeRemoveUser}
                setMode={setModeRemoveUser}
                action={() => {
                  doRemoveUser(selectedUser, true)
                }}
                cancel={(e, setMode) => setMode('')}
                actionTitle="Remove"
                colorAction="error"
                neverShowEdit={true}
              />
            </DialogActions>
          </Dialog>
        </TableContainer> : null}

        {dataRoomInfo && dataRoomInfo.other_users && dataRoomInfo.other_users.length? <TableContainer>
          <Table size='small' className={classes.wMargin}>
            <TableHead>
              <TableRow>
                <TableCell>Other user(s)</TableCell>
                  <Tooltip title='Users can only be added/removed on the User rights page' placement='top-end'>
                    <TableCell className={classes.btnCell}>
                      <AButton startIcon={<AddIcon/>} disabled={true}>Add users</AButton>
                    </TableCell>
                  </Tooltip>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataRoomInfo.other_users.length ? dataRoomInfo.other_users.map((user_i, index) => (
                <TableRow key={user_i.user_name}>
                  <TableCell>{user_i.user_name}</TableCell>
                  <TableCell className={classes.btnCell}>
                    {user_i.more_info && <AButton onClick={() => navigate(`/billing-user/${user_i.user_name}`)} style={{marginRight: 10}}>More info</AButton>}
                  </TableCell>
                </TableRow>
              )) : <TableRow><TableCell className={classes.noUsers}>No users</TableCell><TableCell/></TableRow>}
            </TableBody>
          </Table>
        </TableContainer> : null}
      </GridCard>
    </GridContainer>
  </Layout>
}
